import { useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useController } from 'react-hook-form';
import { Link, useParams } from 'react-router';
import { LuBanknote, LuCalendarDays, LuClock, LuHash, LuHourglass } from 'react-icons/lu';

import { useIdempotencyKey } from '@mds/shared/hooks/use-idempotency-key';
import { ApiError } from '@mds/shared/lib/api';
import { blankAnswers, carryAnswers } from '@mds/shared/lib/form-answers';
import type { GuestEvent, GuestEventRegistrationReceipt } from '@mds/shared/types/guest-event';
import { Alert } from '@mds/shared/ui/feedback/alert';
import { Field } from '@mds/shared/ui/form/field';
import { Form, applyServerErrors, useZodForm } from '@mds/shared/ui/form/form';
import { Button } from '@mds/shared/ui/shadcn/button';
import { Checkbox } from '@mds/shared/ui/shadcn/checkbox';
import { PhoneInput } from '@mds/shared/ui/form/phone-input';
import { Input } from '@mds/shared/ui/shadcn/input';

import AddToCalendar from '@/components/action/add-to-calendar';
import FormQuestions from '@/components/form/form-questions';
import PageHeader from '@/components/heading/page-header';
import OutcomePanel from '@/components/section/outcome-panel';
import { ErrorBlock, LoadingBlock } from '@/components/states';

import { CONTACT } from '@/data';
import { registerForEvent } from '@/features/events/events.api';
import { eventKeys, eventQueries } from '@/features/events/events.queries';
import { registrationSchema } from '@/features/events/registration.schema';
import { formatEventDate, formatMoney } from '@/lib/format';
import NotFoundPage from './not-found';

const band = 'mx-auto max-w-page px-gutter pt-section';
/**
 * The form's column, centred on the page with its header: capped rather than
 * page-wide, since short fields across the whole page left each one half a
 * screen long for a name or a phone number. `box-content` keeps the gutter
 * outside the cap, so the column is the same width at every screen size.
 */
const column = 'mx-auto box-content max-w-3xl px-gutter pt-section';

const timeOf = (event: GuestEvent) => `${event.schedule.startTime} – ${event.schedule.endTime}`;

/** The 409 for a key reused with different details. */
const isReusedKey = (error: unknown) =>
  error instanceof ApiError && error.code === 'EVENT_REGISTRATION_REQUEST_ID_REUSED';

export default function EventRegisterPage() {
  const { slug = '' } = useParams();
  const detail = useQuery(eventQueries.detail(slug));
  const [receipt, setReceipt] = useState<GuestEventRegistrationReceipt | null>(null);

  if (detail.isError && detail.error instanceof ApiError && detail.error.status === 404) {
    return <NotFoundPage />;
  }

  if (detail.isPending || detail.isError) {
    return (
      <section className={band}>
        {detail.isPending ? (
          <LoadingBlock />
        ) : (
          <ErrorBlock
            title="We couldn't load this event"
            error={detail.error}
            onRetry={() => void detail.refetch()}
          />
        )}
      </section>
    );
  }

  const event = detail.data;

  if (receipt) {
    return (
      <div className={band}>
        <Receipt event={event} receipt={receipt} />
      </div>
    );
  }

  return (
    <section className={column}>
      {/* The event at a glance sits under its name: when, and at what price. */}
      <PageHeader
        // A step back to wherever the visitor came from on the site (the home
        // page's featured event, the events list, the event's page); landed
        // here from outside, the event's own page. "Back" rather than the
        // event's name, since the step back is not always to it.
        back={{ label: 'Back', to: `/events/${event.slug}`, history: true }}
        title={`Registration for ${event.title}`}
        meta={[
          formatEventDate(event.schedule.date),
          timeOf(event),
          event.isFree ? 'Free' : `${formatMoney(event.price?.amountMinor ?? 0)} per seat`,
        ]}
      />

      {event.registrationStatus === 'closed' ? (
        // The server refuses a registration once the event has started, so
        // there is no form to fill in only to be refused. A full event still
        // takes registrations: they join the waitlist.
        <div className="mt-block max-w-prose">
          <Alert tone="neutral" title="Registration has closed">
            This event has already started.
          </Alert>
          <Button asChild variant="outline" size="lg" className="mt-6">
            <Link to="/events">See other events</Link>
          </Button>
        </div>
      ) : (
        <RegistrationForm event={event} onReceipt={setReceipt} />
      )}
    </section>
  );
}

function RegistrationForm({
  event,
  onReceipt,
}: {
  event: GuestEvent;
  onReceipt: (receipt: GuestEventRegistrationReceipt) => void;
}) {
  const queryClient = useQueryClient();
  /**
   * The copy of the event's questions these fields were drawn from. It moves
   * on only when the event is read again with a newer one — after the server
   * refuses answers to this copy — and the answers move with it.
   */
  const [questions, setQuestions] = useState(event.registrationForm);
  /** Set when the questions changed under the visitor, until they send again. */
  const [questionsChanged, setQuestionsChanged] = useState(false);
  const schema = useMemo(() => registrationSchema(questions), [questions]);
  const form = useZodForm(schema, {
    defaultValues: {
      attendeeName: '',
      attendeeEmail: '',
      attendeePhone: '',
      joinMailingList: false,
      answers: blankAnswers(questions),
    },
  });

  // A newer copy arrived: keep every answer whose question kept its wording
  // and type, and drop picks the question no longer offers.
  const latest = event.registrationForm;
  useEffect(() => {
    if (latest.version === questions.version) return;
    form.setValue('answers', carryAnswers(questions, form.getValues('answers'), latest));
    setQuestions(latest);
    setQuestionsChanged(true);
  }, [latest, questions, form]);

  // A checkbox reads `checked`, not `value`, so Field cannot bind it by name.
  const mailingList = useController({ control: form.control, name: 'joinMailingList' });
  // A retry of the same details reuses the key — after a lost response, even
  // after a reload — so the visitor is never registered twice. Only the key
  // and a fingerprint are stored; the details themselves are not.
  const idempotency = useIdempotencyKey(`idem:event-registration:${event.id}`);
  /** A failure no field can show. `null` once dismissed or a new send starts. */
  const [formError, setFormError] = useState<unknown>(null);
  /** The server already has this email and phone on the list: not a failure. */
  const [duplicate, setDuplicate] = useState(false);

  /** Seats left and the status move with every registration: read them again. */
  const refreshEvent = () => queryClient.invalidateQueries({ queryKey: eventKeys.all });

  const registering = useMutation({
    mutationFn: (body: Parameters<typeof registerForEvent>[1]) => registerForEvent(event.id, body),
    onMutate: () => {
      setFormError(null);
      setDuplicate(false);
      setQuestionsChanged(false);
    },
    onSuccess: (receipt) => {
      idempotency.reset();
      refreshEvent();
      onReceipt(receipt);
    },
    onError: (error) => {
      // Field mistakes the form's own checks did not catch land under the field.
      if (applyServerErrors(error, form.setError)) return;

      const code = error instanceof ApiError ? error.code : undefined;
      if (code === 'EVENT_REGISTRATION_FORM_CHANGED') {
        // Staff edited the questions while this form was open. Reading the
        // event again brings the new copy; the effect above carries the
        // answers over and says so.
        refreshEvent();
        return;
      }
      if (code === 'EVENT_REGISTRATION_CLOSED') {
        // The event started while the form was open; the page switches to its
        // closed state once the event is read again.
        refreshEvent();
        return;
      }
      if (code === 'EVENT_REGISTRATION_DUPLICATE') {
        // Registered already, under these details: what they wanted is done.
        setDuplicate(true);
        return;
      }
      if (code === 'EVENT_REGISTRATION_REQUEST_ID_REUSED') {
        // The key went out earlier with different details. A new one is made
        // on the next press; the visitor only has to press again.
        idempotency.reset();
      }
      setFormError(error);
    },
  });

  return (
    <>
      <Form
        form={form}
        className="mt-block flex flex-col gap-card"
        onSubmit={(values) => {
          const body = {
            attendeeName: values.attendeeName,
            attendeeEmail: values.attendeeEmail,
            attendeePhone: values.attendeePhone,
            // No `seatCount`: one registration is one seat, the API's default.
            // The copy of the questions these answers are to; the server
            // refuses answers to an older one.
            formVersion: questions.version,
            answers: values.answers,
            joinMailingList: values.joinMailingList,
          };
          registering.mutate({ requestId: idempotency.keyFor(body), ...body });
        }}
      >
        {/* The attendee's details open the first card; the event's own
            questions, when staff added any, follow it and each section after.
            The mailing-list opt-in closes the last card, whichever it is. */}
        <FormQuestions
          form={questions}
          title="Your details"
          footer={
            <label className="flex cursor-pointer items-center gap-3 text-base">
              <Checkbox
                checked={mailingList.field.value}
                onCheckedChange={(state) => mailingList.field.onChange(state === true)}
                onBlur={mailingList.field.onBlur}
              />
              Email me about upcoming MDS events
            </label>
          }
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <Field name="attendeeName" label="Full name" required>
              <Input autoComplete="name" />
            </Field>
            <Field
              name="attendeeEmail"
              label="Email"
              required
              hint={
                event.isFree ? 'Your ticket arrives here.' : 'Your ticket arrives here once paid.'
              }
            >
              <Input type="email" autoComplete="email" />
            </Field>
            <Field
              name="attendeePhone"
              label="Phone / WhatsApp"
              required
              hint="So we can reach you if plans change."
            >
              <PhoneInput />
            </Field>
          </div>
        </FormQuestions>

        {event.registrationStatus === 'full' ? (
          // Said before pressing Register, not after: a waitlist place is not
          // a seat, and the visitor should choose knowing that.
          <Alert tone="warning" title="This event is full">
            You can still register to join the waitlist. Nothing is held and nothing is due yet. If
            a seat opens, MDS will contact you.
          </Alert>
        ) : (
          !event.isFree && (
            // Only a confirmed registration takes a seat, and a paid one is
            // confirmed when it is paid — worth knowing before pressing Register.
            <Alert tone="neutral" title="Paid at the front desk">
              Registering reserves nothing yet: your seat is held once the full amount is paid at
              MDS, and your ticket is emailed then.
            </Alert>
          )
        )}

        {questionsChanged && (
          <Alert tone="warning" title="The questions changed">
            MDS updated this form while you were filling it in. We kept the answers that still
            apply. Check them, then press Register again.
          </Alert>
        )}

        {duplicate && (
          <Alert tone="neutral" title="You're already registered" onDismiss={() => setDuplicate(false)}>
            This email and phone number are already on the list for {event.title}. Check your email
            for the details we sent when you registered.
          </Alert>
        )}

        {formError !== null && (
          <ErrorBlock
            title="We couldn't register you"
            error={formError}
            message={
              isReusedKey(formError)
                ? 'Your details changed since the last try. Press Register again.'
                : undefined
            }
            onDismiss={() => setFormError(null)}
          />
        )}

        <div className="flex flex-wrap justify-center gap-3">
          <Button type="submit" size="lg" className="min-w-56" disabled={registering.isPending}>
            {registering.isPending
              ? 'Registering…'
              : event.registrationStatus === 'full'
                ? 'Join the waitlist'
                : 'Register'}
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to={`/events/${event.slug}`}>Back</Link>
          </Button>
        </div>
      </Form>
    </>
  );
}

/**
 * What happened, in place of the form — a form still sitting there invites
 * sending it twice.
 *
 * Read from the receipt's `reservationPolicy` and `ticketDelivery`, never from
 * the price: the server decides whether seats are held and when the ticket
 * goes out, and the backend's contract says not to infer either here.
 */
function Receipt({
  event,
  receipt,
}: {
  event: GuestEvent;
  receipt: GuestEventRegistrationReceipt;
}) {
  const { reservationPolicy, ticketDelivery, price } = receipt;
  const firstName = receipt.attendeeName.split(' ')[0];
  const email = <b className="font-medium text-ink">{ticketDelivery.email}</b>;
  const waitlisted = receipt.status === 'waitlisted';

  const outcome = waitlisted
    ? {
        tone: 'warning' as const,
        // An hourglass rather than the warning triangle: nothing is wrong, it is a wait.
        icon: LuHourglass,
        title: `You're on the waitlist, ${firstName}`,
        description: (
          <>
            {event.title} is full, so nothing is held and nothing is due yet. If a seat opens, MDS
            will contact you, and your ticket goes to {email} once you're in.
          </>
        ),
      }
    : reservationPolicy.holdsSeats
      ? {
          tone: 'success' as const,
          icon: undefined,
          title: `You're in, ${firstName}`,
          description: (
            <>
              Your seat is held for you.
              {ticketDelivery.queued && <> Your QR ticket is on its way to {email}.</>}
            </>
          ),
        }
      : reservationPolicy.requiresPayment
        ? {
            tone: 'warning' as const,
            // A clock rather than the warning triangle: nothing is wrong, one step is left.
            icon: LuClock,
            title: `One step left, ${firstName}`,
            description: (
              <>
                You're registered. Pay{' '}
                <b className="font-medium text-ink">
                  {formatMoney(reservationPolicy.fullPaymentAmountMinor)}
                </b>{' '}
                at the MDS front desk to hold your seat; your QR ticket is emailed to {email} then.
              </>
            ),
          }
        : {
            tone: 'neutral' as const,
            icon: undefined,
            title: 'Registration received',
            description: <>We have your registration for {event.title}.</>,
          };

  return (
    <OutcomePanel
      tone={outcome.tone}
      icon={outcome.icon}
      title={outcome.title}
      description={outcome.description}
      details={{
        title: event.title,
        items: [
          { icon: LuHash, marker: 'Reference', description: receipt.reference },
          {
            icon: LuCalendarDays,
            marker: 'Date',
            description: formatEventDate(event.schedule.date),
          },
          { icon: LuClock, marker: 'Time', description: timeOf(event) },
          {
            icon: LuBanknote,
            marker: 'Price',
            description:
              price.totalAmountMinor === 0 ? 'Free' : formatMoney(price.totalAmountMinor),
          },
        ],
        // The event's own conditions for its price, when it has any.
        note: event.priceNote || undefined,
      }}
      actions={
        <>
          {/* Not for a waitlist place: a calendar entry reads as a promise of a seat. */}
          {!waitlisted && (
            <AddToCalendar
              event={{
                uid: `${receipt.reference}@mds`,
                title: event.title,
                date: event.schedule.date,
                startTime: event.schedule.startTime,
                endTime: event.schedule.endTime,
                // The building, so maps can find it; the room goes in the notes.
                location: CONTACT.addressLines.join(', '),
                description: `${event.space} · Reference ${receipt.reference}`,
              }}
            />
          )}
          <Button asChild variant="outline" size="lg">
            <Link to="/events">Back to events</Link>
          </Button>
        </>
      }
    />
  );
}
